import { withErrorBoundary } from 'app/components/error-boundary';
import { useTutorials } from 'app/components/tutorial-overlay';
import { CheckBoxPositif } from 'app/design/icons/checkbox-positif';
import { Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';

import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import { QUICK_NEW_ITEMS_KEY, useQuickOffers } from 'app/hooks/use-quickoffers';
import { Event, track } from 'app/lib/analytics';
import { getI18nLanguage } from 'app/lib/language';
import { useAllAddresses } from 'app/network/address';
import { useListingVisibilityGroups } from 'app/network/groups';
import { useCreateQuickListings } from 'app/network/listing';
import { useTrialFreeSubAvailable } from 'app/network/promotions';
import { Signal, hasSignal } from 'app/network/signals';
import { GroupsSection } from 'app/screens/new/components/groups-section';
import { popularItemSchema, type PopularItem } from 'app/types/category';
import type { CreateListingDTO } from 'app/types/listing';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { usePageTitle, useUISize } from 'app/utils/web-helpers';
import React, { useEffect, useReducer } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';

import { IconArrowRight } from '@tabler/icons-react-native';
import { useTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { usePresentAppDownloadPrompt } from 'app/hooks/use-present-app-download-prompt';

function ConfirmAccessory({
  backgroundColor,
  formIsValid,
  isLargeUI,
  isPending,
  mutationError,
  numSelectedItems,
  onBack,
  onSubmit,
}: {
  backgroundColor: string;
  formIsValid: boolean;
  isLargeUI: boolean;
  isPending: boolean;
  mutationError: ReturnType<typeof useCreateQuickListings>['error'];
  numSelectedItems: number;
  onBack: () => void;
  onSubmit: () => Promise<void>;
}) {
  const { t } = useScopedTranslation();
  const { t: tAPI } = useTranslation('api-errors');
  const isMutuo = isWhiteLabel(WhiteLabel.Mutuo);

  return (
    <Accessory style={{ backgroundColor }}>
      {mutationError ? (
        <View style={{ marginHorizontal: 20 }}>
          <RemoteError error={mutationError} message={tAPI(mutationError.message)} />
        </View>
      ) : null}

      <Row justifyContent={isLargeUI ? 'flex-end' : 'center'} alignItems="center" gap="spacing.general.x2">
        {isLargeUI ? (
          <Button onPress={onBack} variant={isMutuo ? 'whiteWithBorder' : 'secondary'}>
            <Text color="text.primary" fontWeight="500" textAlign="center">
              {t('secondaryButtonText')}
            </Text>
          </Button>
        ) : null}

        <Button
          disabled={!formIsValid}
          loading={isPending}
          marginHorizontal="spacing.general.x4"
          marginTop={isLargeUI ? 'spacing.general.0' : 'spacing.general.x30'}
          onPress={() => void onSubmit()}
          style={!isLargeUI ? { flex: 1 } : undefined}
          variant={isMutuo ? 'whiteWithBorder' : 'primary'}
        >
          <Text
            color={isMutuo ? 'text.primary' : 'surface.default'}
            fontWeight={formIsValid ? '700' : '500'}
            textAlign="center"
          >
            {t('buttonText', { num: numSelectedItems })}
          </Text>
        </Button>
      </Row>
    </Accessory>
  );
}

const ConfirmScreenComponent: NavigationScreen<'quick-new-confirm'> = ({ navigation, route }) => {
  const theme = useTheme();
  const { t } = useScopedTranslation();
  const i18nLanguage = getI18nLanguage();
  const groupsQuery = useListingVisibilityGroups(true);
  const addressesQuery = useAllAddresses();
  const { isLargeUI } = useUISize();
  const presentAppDownloadPrompt = usePresentAppDownloadPrompt();

  const setPageTitle = usePageTitle();
  useEffect(
    () => setPageTitle(`${t('headerTitle')} | ${t('headerTitle', { ns: 'quick-new-choose' })}`),
    [setPageTitle, t],
  );
  const bgColor = isWhiteLabel()
    ? theme.colors['surface.inverse.tertiary-neutral']
    : theme.colors['white-label.splash'];
  const cardColor = isWhiteLabel() ? theme.colors['text.primary'] : theme.colors['text.brand'];
  useNavigationOptions(navigation, {
    headerLeft: () => (
      <Button onPress={() => navigation.goBack()}>
        <IconArrowRight
          strokeWidth={ICON_STROKE_WIDTH}
          color={isWhiteLabel(WhiteLabel.Mutuo) ? 'white' : 'transparent'}
        />
      </Button>
    ),
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color={isWhiteLabel(WhiteLabel.Mutuo) ? 'white' : 'text.primary'}>
        {t('headerTitle')}
      </Text>
    ),
    headerStyle: { backgroundColor: bgColor },
  });
  interface FormSchema {
    addressIds?: string[];
    groupIds?: string[];
    // Add dynamic keys for each selected item
    [key: `row_${string}`]: boolean;
  }

  const formFields = useForm<FormSchema>({
    mode: 'onChange',
    defaultValues: {
      addressIds: addressesQuery.data?.map((a) => a.id) ?? [],
      groupIds: groupsQuery.data?.map((g) => g.id) ?? [],
    },
  });
  const { handleSubmit } = formFields;

  const [selectedItems] = useReducer(
    (items: PopularItem[]) => items,
    null,
    (): PopularItem[] => {
      let items: unknown = (route.params as { selectedItems?: unknown } | undefined)?.selectedItems;
      if (!items || !Array.isArray(items)) {
        if (Platform.OS === 'web') {
          try {
            const stored = sessionStorage.getItem(QUICK_NEW_ITEMS_KEY);
            if (stored) items = JSON.parse(stored) as unknown;
          } catch {}
        }
      }
      if (!items || !Array.isArray(items)) return [];
      return items.map((popItem) => {
        return popularItemSchema.cast({
          id: (popItem.id as string | undefined) ?? '',
          en: popItem.en,
          fr: popItem.fr,
          es: popItem.es,
          selected: true,
          categoryId: popItem.categoryId,
          pictureUrl: popItem.pictureUrl,
        });
      });
    },
  );

  const numSelectedItems = selectedItems.filter((item) => item.selected).length;
  const formIsValid = numSelectedItems > 0;

  const { continueTutorial, postponeSignal } = useTutorials();

  const trialFreeSubAvailable = useTrialFreeSubAvailable(navigation);

  // Crete quick offers on API
  const {
    error: mutationError,
    mutate,
    isPending,
  } = useCreateQuickListings({
    async onSuccess(listings) {
      const stillSelectedItems = selectedItems.filter((item) => item.selected);
      track(Event.QuickOffersPosted, {
        num_items: stillSelectedItems.length,
        items: stillSelectedItems,
      });

      const listing = listings[0];

      const { shouldShowFreeSubScreen, navigateToFreeSubScreen, freeSubReceived } = trialFreeSubAvailable(listing);

      const userNowContributor = hasSignal(listing, Signal.UserLevelNowContributor);

      if (await continueTutorial({ numListingsPublished: stillSelectedItems.length })) {
        // Postpone signals until end of tutorial
        if (userNowContributor) {
          postponeSignal(Signal.UserLevelNowContributor);
        }
        if (freeSubReceived) {
          postponeSignal(Signal.TriggeredFreeSub);
        }
        return;
      }

      if (shouldShowFreeSubScreen) {
        navigateToFreeSubScreen();
      } else {
        // Continue to the next screen
        goToNext({ userNowContributor, listingIds: listings.map((l) => l.id) });
      }
      presentAppDownloadPrompt({ context: 'listing-published' });
    },
  });

  const { goToNext } = useQuickOffers();
  const onSubmit = handleSubmit((form) => {
    const groupIds = form.groupIds;

    // If the user is in a group, they saw the <GroupsSection> component to select where to post the listing(s), and they could, in theory, select neither group nor neighbourhood.
    // Otherwise (when !userIsInGroups), then we need to send the user's address in addressIds, because they didn't see the <GroupsSection> component.
    const userIsInGroup = (groupsQuery.data?.length ?? 0) > 0;
    const addressIds =
      (form.addressIds?.length ?? 0) > 0 ? form.addressIds : userIsInGroup ? [] : addressesQuery.data!.map((a) => a.id);

    const payload = selectedItems.flatMap<CreateListingDTO>((popItem) =>
      popItem.selected
        ? [
            {
              item: { name: popItem[i18nLanguage] },
              type: ListingType.offer,
              subtype: ListingSubtype.object,
              categoryPrimaryId: popItem.categoryId,
              serviceCategoryId: null,
              groupIds: groupIds!,
              addressIds: addressIds!,
              description: null,
              start: null,
              end: null,
              picture: null,
              pictureUrl: popItem.pictureUrl,
              ...(isWhiteLabel(WhiteLabel.HauteGaspesie) && { transactionMode: TransactionMode.lend }),
            },
          ]
        : [],
    );

    mutate({ listings: payload });
  });

  return (
    <Page
      accessory={
        <ConfirmAccessory
          backgroundColor={bgColor}
          formIsValid={formIsValid}
          isLargeUI={isLargeUI}
          isPending={isPending}
          mutationError={mutationError}
          numSelectedItems={numSelectedItems}
          onBack={() => navigation.goBack()}
          onSubmit={onSubmit}
        />
      }
      contentContainerStyle={{ backgroundColor: bgColor, gap: theme.spacing['spacing.general.x2'] }}
      style={{ backgroundColor: bgColor }}
    >
      <View
        style={{
          alignItems: 'center',
          width: '75%',
          maxWidth: 350,
          aspectRatio: 343 / 180,
          backgroundColor: cardColor,
          borderRadius: theme.borderRadii['radius.xl'],
          justifyContent: 'center',
          alignSelf: 'center',
        }}
      >
        <CheckBoxPositif color={bgColor} />
      </View>
      <Text
        style={{ color: isWhiteLabel(WhiteLabel.Mutuo) ? '#FFFFFF' : theme.colors['text.primary'] }}
        variant="headerXSmall"
        fontSize={36}
        fontWeight="700"
        marginHorizontal="spacing.general.x8"
        marginTop="spacing.general.x2"
        textAlign="center"
      >
        {t('almostThere')}
      </Text>
      <Text
        style={{ color: isWhiteLabel(WhiteLabel.Mutuo) ? '#FFFFFF' : theme.colors['text.primary'] }}
        fontSize={18}
        lineHeight={22}
        marginHorizontal="spacing.general.x2"
        marginTop="spacing.general.x2"
        textAlign="center"
      >
        <Trans
          components={{
            strong: <Text fontWeight="700" color={isWhiteLabel(WhiteLabel.Mutuo) ? 'white' : 'text.primary'} />,
          }}
          i18nKey="headerText"
          ns="quick-new-confirm"
        />
      </Text>

      <FormProvider {...formFields}>
        <View>
          <GroupsSection includeAllSectorGroups />
        </View>
      </FormProvider>
    </Page>
  );
};

export const ConfirmScreen: NavigationScreen<'quick-new-confirm'> = withErrorBoundary(ConfirmScreenComponent);
